var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { JSXToArray, JSXToText, findWordIndex, splitElement } from "./utils.js";
import { HiMiniStop, HiVolumeOff, HiVolumeUp } from "./icons.js";
export default function Speech({ text, id, pitch = 1, rate = 1, volume = 1, lang, voiceURI, startBtn = React.createElement(HiVolumeUp, null), pauseBtn = React.createElement(HiVolumeOff, null), stopBtn = React.createElement(HiMiniStop, null), useStopOverPause, highlightText = false, highlightProps = { style: { backgroundColor: "yellow" } }, onError = () => alert("Browser not supported! Try some other browser."), props = {}, children, }) {
    const [speechStatus, setSpeechStatus] = useState("stopped");
    const [useStop, setUseStop] = useState();
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const [highlightedLength, setHighlightedLength] = useState(0);
    const [highlightContainer, setHighlightContainer] = useState(null);
    const words = useMemo(() => JSXToArray(text), [text]);
    const pause = () => { var _a; return speechStatus !== "paused" && ((_a = window.speechSynthesis) === null || _a === void 0 ? void 0 : _a.pause()); };
    const stop = () => { var _a; return speechStatus !== "stopped" && ((_a = window.speechSynthesis) === null || _a === void 0 ? void 0 : _a.cancel()); };
    function start() {
        const synth = window.speechSynthesis;
        if (!synth)
            return onError();
        setSpeechStatus("started");
        if (speechStatus === "paused")
            return synth.resume();
        if (synth.speaking)
            synth.cancel();
        const utterance = new window.SpeechSynthesisUtterance(JSXToText(text));
        utterance.pitch = pitch;
        utterance.rate = rate;
        utterance.volume = volume;
        if (lang)
            utterance.lang = lang;
        if (voiceURI) {
            if (!Array.isArray(voiceURI))
                voiceURI = [voiceURI];
            const voices = synth.getVoices();
            for (let i = 0; i < voiceURI.length; i++) {
                const uri = voiceURI[i];
                const voice = voices.find((voice) => voice.voiceURI === uri);
                if (voice) {
                    utterance.voice = voice;
                    break;
                }
            }
        }
        function setStopped() {
            setSpeechStatus("stopped");
            setHighlightedIndex(null);
            setHighlightedLength(0);
            utterance.onpause = null;
            utterance.onend = null;
            utterance.onerror = null;
            utterance.onboundary = null;
            if (synth.paused)
                synth.cancel();
        }
        utterance.onpause = () => setSpeechStatus("paused");
        utterance.onend = setStopped;
        utterance.onerror = setStopped;
        if (highlightText)
            utterance.onboundary = ({ charIndex, charLength }) => {
                setHighlightedIndex(findWordIndex(words, charIndex));
                setHighlightedLength(charLength);
            };
        synth.speak(utterance);
    }
    function highlightedText(element, parentIndex = "") {
        var _a;
        if (!(highlightedIndex === null || highlightedIndex === void 0 ? void 0 : highlightedIndex.startsWith(parentIndex)))
            return element;
        if (Array.isArray(element))
            return element.map((child, index) => highlightedText(child, parentIndex === "" ? `${index}` : `${parentIndex}-${index}`));
        if (isValidElement(element))
            return cloneElement(element, { key: (_a = element.key) !== null && _a !== void 0 ? _a : Math.random().toString() }, highlightedText(element.props.children, parentIndex));
        if (typeof element === "string" || typeof element === "number") {
            element = element.toString();
            const index = +highlightedIndex.split("-").at(-1);
            const before = index
                ? splitElement(element)
                    .slice(0, index)
                    .join("").length
                : 0;
            return (React.createElement(Fragment, { key: highlightedIndex },
                element.slice(0, before),
                React.createElement("span", Object.assign({}, highlightProps), element.slice(before, before + highlightedLength)),
                element.slice(before + highlightedLength)));
        }
        return element;
    }
    useEffect(() => {
        var _a;
        setUseStop(useStopOverPause !== null && useStopOverPause !== void 0 ? useStopOverPause : (((_a = navigator.userAgentData) === null || _a === void 0 ? void 0 : _a.mobile) || false));
        return () => { var _a; return (_a = window.speechSynthesis) === null || _a === void 0 ? void 0 : _a.cancel(); };
    }, []);
    useEffect(() => {
        if (highlightText)
            setHighlightContainer(document.getElementById(`rtts-${id}`));
        else
            setHighlightContainer(null);
    }, [highlightText]);
    return (React.createElement(React.Fragment, null,
        typeof children === "function" ? (children({ speechStatus, start, pause, stop })) : (React.createElement("div", Object.assign({ style: { display: "flex", columnGap: "1rem" } }, props),
            speechStatus !== "started" ? (React.createElement("span", { role: "button", onClick: start }, startBtn)) : useStop === false ? (React.createElement("span", { role: "button", onClick: pause }, pauseBtn)) : (React.createElement("span", { role: "button", onClick: stop }, stopBtn)),
            useStop === false && stopBtn && (React.createElement("span", { role: "button", onClick: stop }, stopBtn)))),
        highlightContainer && createPortal(highlightedText(text), highlightContainer)));
}
export function HighlightedText(_a) {
    var { id, children } = _a, props = __rest(_a, ["id", "children"]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(false);
    }, []);
    return (React.createElement("div", Object.assign({ id: `rtts-${id}` }, props), loading && children));
}
